import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import '@ant-design/v5-patch-for-react-19';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { AvatarDropdown, AvatarName, Question, SelectLang } from '@/components';
import type { IUserInfo } from '@/services/user/getUserInfo';
import { getUserInfo } from '@/services/user/getUserInfo';
import { parseAsync } from '@/utils/parseAsync';
import defaultSettings from '../config/defaultSettings';
import { UtcTime } from './components/RightContent';
import { errorConfig } from './services/requestErrorConfig';
import { getCurrentLocale, setCurrentLocale } from './utils/i18n';

window._APP_BUILD_TIME_ = APP_BUILD_TIME;

const _isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

// 默认用户图片
const defaultAvatar = 'https://gw.alipayobjects.com/zos/rmsportal/BiazfanxmamNRoxxVxka.png';

// 本地存储的 key
const LAYOUT_SETTINGS_KEY = 'LAYOUT_SETTINGS';

/**
 * 从本地存储获取样式配置
 */
const getSettingsFromStorage = (): Partial<LayoutSettings> | null => {
  try {
    const stored = localStorage.getItem(LAYOUT_SETTINGS_KEY);
    if (stored) {
      return JSON.parse(stored) as Partial<LayoutSettings>;
    }
  } catch (error) {
    console.error('读取本地样式配置失败:', error);
  }
  return null;
};

/**
 * 保存样式配置到本地存储
 */
const saveSettingsToStorage = (settings: Partial<LayoutSettings>) => {
  try {
    localStorage.setItem(LAYOUT_SETTINGS_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('保存样式配置到本地失败:', error);
  }
};

interface IInitialState {
  settings?: Partial<LayoutSettings>;
  userInfo?: IUserInfo;
  loading?: boolean;
  fetchUserInfo?: () => Promise<IUserInfo | undefined>;
}

const fetchUserInfo = async () => {
  const [err, res] = await parseAsync(getUserInfo());
  if (err) {
    history.push(loginPath);
    return;
  }

  return res.data;
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<IInitialState> {
  // 如果不是登录页面，执行
  const { location } = history;
  const locale = getCurrentLocale();
  console.log('🚀 ~ getInitialState ~ locale: ', locale);
  // 别问我为什么这样写，因为我疯了，我也不知道为什么，我只能这样写
  if (locale === 'en-US') {
    setCurrentLocale('en-US');
  }
  // 从本地存储读取样式配置，如果没有则使用默认配置
  const storedSettings = getSettingsFromStorage();
  const settings = (storedSettings || defaultSettings) as Partial<LayoutSettings>;

  if (![loginPath, '/user/register', '/user/register-result'].includes(location.pathname)) {
    const userInfo = await fetchUserInfo();
    console.log('🚀 ~ getInitialState ~ userInfo: ', userInfo);

    return {
      fetchUserInfo,
      userInfo,
      settings,
    };
  }

  return {
    fetchUserInfo,
    settings,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    actionsRender: () => [<UtcTime key="UtcTime" />, <Question key="doc" />, <SelectLang key="SelectLang" />],
    avatarProps: {
      src: defaultAvatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.userInfo?.user?.userName,
    // },
    // footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.userInfo && location.pathname !== loginPath) {
        history.push(loginPath);
        return;
      }

      // 如果已登录，检查密码是否是初始密码
      if (initialState?.userInfo && initialState.userInfo.user.isInitPassword === 1) {
        // 如果当前不在修改密码页面，则强制跳转
        if (location.pathname !== '/user/init-password') {
          history.replace('/user/init-password');
        }
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          <SettingDrawer
            disableUrlParams
            enableDarkTheme
            settings={initialState?.settings}
            hideHintAlert
            hideCopyButton
            onCollapseChange={(collapse) => {
              console.log('🚀 ~ onCollapseChange ~ collapse: ', collapse);
            }}
            onSettingChange={(settings) => {
              console.log('🚀 ~ onSettingChange ~ settings: ', settings);
              // 保存样式配置到本地存储
              saveSettingsToStorage(settings);
              setInitialState((preInitialState) => ({
                ...preInitialState,
                settings,
              }));
            }}
          />
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: getCurrentRequestUrl(),
  timeout: TIMEOUT,
  ...errorConfig,
}; /**
 * 获取当前的请求地址
 * @returns 当前浏览器URL的协议、主机名和端口 + BASE_URL
 * http://api.bitbaby.shop/partners  test
 * https://web-api.bitbabyuat.com/partners  uat
 * https://api.bitbaby.com/partners prod
 */

function getCurrentRequestUrl() {
  const _origin = 'https://api.bitbaby.com/partners';

  return `${_origin}${BASE_URL}`;
}
